import { Row, Select, Space, Table, Typography } from "antd";
import { useFetchFixturesByTaskStatus } from "hooks/useFixturesApi";
import { useFetchFixtureSeasonsByTaskStatus } from "hooks/useSeasonsApi";
import { useState } from "react";
import { useIntl } from "react-intl";
import { createPagination } from "utils/createPagination";
import { useAdminFixturesColumns } from "../hooks/useAdminFixturesColumns";
import { SelectTaskStatus } from "./SelectTaskStatus";

const DefaultSeasonFilterOptionValue = "DEFAULT";

type Props = {
  defaultTaskStatusOptionValue: string;
  pageSize: number;
};

export const AdminFixtureList = ({ pageSize, defaultTaskStatusOptionValue }: Props) => {
  const { formatMessage } = useIntl();
  const [taskStatus, setTaskStatus] = useState(defaultTaskStatusOptionValue);
  const [seasonId, setSeasonId] = useState<string | undefined>(undefined);
  const [currentPage, setCurrentPage] = useState(1);

  const { isLoading: isSeasonsLoading, data: seasonsResponse } = useFetchFixtureSeasonsByTaskStatus({ taskStatus });
  const seasons = seasonsResponse?.data || [];
  const { isLoading: isFixturesLoading, data: fixturesResponse } = useFetchFixturesByTaskStatus({
    seasonId,
    taskStatus: taskStatus,
    page: currentPage,
    pageSize: pageSize,
  });
  const fixtures = fixturesResponse?.data || [];
  const total = fixturesResponse?.meta?.count || 0;

  const columns = useAdminFixturesColumns();
  const pagination = createPagination(total, pageSize, currentPage);

  const seasonFilterOptions = [
    {
      label: formatMessage({ id: "select.seasons.defaultOption.label" }),
      value: DefaultSeasonFilterOptionValue,
    },
    ...seasons.map((item) => ({
      label: item?.nameLocal,
      value: item?.seasonId,
    })),
  ];

  const onTaskStatusFilterChanged = (value: string) => {
    setTaskStatus(value);
    setSeasonId(undefined);
    setCurrentPage(1);
  };

  const onSeasonFilterChanged = (value: string) => {
    setSeasonId(value === DefaultSeasonFilterOptionValue ? undefined : value);
    setCurrentPage(1);
  };

  return (
    <>
      <Row justify="space-between" align="middle">
        <Typography.Title level={5}>{formatMessage({ id: "adminDashboardPage.fixtureTasks.title" })}</Typography.Title>
        <Space>
          <Select
            placeholder="Select season"
            popupMatchSelectWidth={false}
            onChange={onSeasonFilterChanged}
            defaultValue={DefaultSeasonFilterOptionValue}
            showSearch
            loading={isSeasonsLoading}
            filterOption={(value, option) => (option?.label ?? "").toLowerCase().includes(value.toLowerCase())}
            options={seasonFilterOptions}
          />
          <SelectTaskStatus defaultOptionValue={defaultTaskStatusOptionValue} onChange={onTaskStatusFilterChanged} />
        </Space>
      </Row>
      <Table
        dataSource={fixtures}
        size="small"
        pagination={pagination}
        onChange={(p: any) => setCurrentPage(p.current)}
        rowKey={(record: any) => record?.taskEntityId + record?.fixtureId}
        loading={isFixturesLoading}
        columns={columns as any}
      />
    </>
  );
};
